/**
 * Two kinds of error, named so the split can be enforced instead of remembered.
 *
 * A `DomainError` (in `@repo/domain`) is a FAILURE: a rule said no, and the
 * caller can act on the answer — pick another slot, supply the field, stop
 * asking. It is ordinary control flow and it earns a 4xx.
 *
 * An `EngineDefect` is data or state that should be impossible: a stored row no
 * mapper can read, a graph that contradicts the correspondence its mapper
 * declares, a notification from a resource nothing has been migrated to handle.
 * No different request makes it go away, so it is a 500 and it is loud. Domain
 * logic does not catch these to recover; it catches them to classify.
 *
 * A shared parent is not a shared identity: the `MappingProblem` note below
 * still stands. Route on `instanceof EngineDefect` to decide DISPOSITION ("is
 * this a defect?"), never to tell one defect from another — that is what a code
 * is for.
 */
export abstract class EngineDefect extends Error {
  /**
   * Nominal marker. Without a member of its own, every `Error` satisfies this
   * shape, and the exhaustiveness check in `middleware/error-handler.ts` — which
   * relies on the two bases being disjoint — goes slack without failing.
   */
  readonly defect = true;
}

/**
 * What went wrong translating a stored resource, as a value rather than as
 * prose. The domain's errors carry a `code` and typed `details`; these did not,
 * so every site hand-wrote its own phrasing for the same situation and nothing
 * downstream could tell "a required field is absent" from "a stored code is not
 * one the domain has".
 *
 * A code rather than a subclass, deliberately: `projection/walk.ts` routes by
 * `instanceof`, so a new sibling class that nobody remembered to add there
 * escapes the walk and turns a bad row from a classified skip into a crashed
 * projection. One class keeps that branch correct however many problems we name.
 */
export type MappingProblem = "FIELD_MISSING" | "VALUE_OUT_OF_RANGE" | "IDENTITY_AMBIGUOUS";

export interface MappingDetails {
  resourceType: string;
  /** Absent on a row the server has not assigned an id yet. */
  id?: string;
  /** The domain field being read, not the FHIR path it came from. */
  field?: string;
  found?: unknown;
  allowed?: readonly string[];
}

/** Persisted FHIR cannot be translated into the domain shape its mapper promises. */
export class PersistenceMappingError extends EngineDefect {
  readonly code?: MappingProblem;
  readonly details?: Readonly<MappingDetails>;

  constructor(message: string, code?: MappingProblem, details?: MappingDetails) {
    super(message);
    this.name = "PersistenceMappingError";
    if (code) this.code = code;
    if (details) this.details = details;
  }
}

/** A loaded resource graph does not match the mapper's declared correspondence. */
export class CorrespondenceError extends EngineDefect {
  constructor(message: string) {
    super(message);
    this.name = "CorrespondenceError";
  }
}
